import React, { useEffect, useState } from "react";
import { Activity, TrendingUp, AlertCircle, Loader2 } from "lucide-react";

interface WorkflowStatusCardProps {
  totalWorkflow: number;
  processDone: number;
  processFail: number;
  processInProgress: number;
}

const WorkflowStatusCard: React.FC<WorkflowStatusCardProps> = ({
  totalWorkflow,
  processDone,
  processFail,
  processInProgress,
}) => {
  const [animatedDone, setAnimatedDone] = useState(0);
  const [animatedFail, setAnimatedFail] = useState(0);
  const [animatedInProgress, setAnimatedInProgress] = useState(0);
  const [animatedTotal, setAnimatedTotal] = useState(0);

  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedTotal(Math.floor(totalWorkflow * progress));
      setAnimatedDone(Math.floor(processDone * progress));
      setAnimatedFail(Math.floor(processFail * progress));
      setAnimatedInProgress(Math.floor(processInProgress * progress));

      if (currentStep >= steps) {
        setAnimatedTotal(totalWorkflow);
        setAnimatedDone(processDone);
        setAnimatedFail(processFail);
        setAnimatedInProgress(processInProgress);
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [totalWorkflow, processDone, processFail, processInProgress]);

  const donePercentage = totalWorkflow
    ? (processDone / totalWorkflow) * 100
    : 0;
  const failPercentage = totalWorkflow
    ? (processFail / totalWorkflow) * 100
    : 0;
  const inProgressPercentage = totalWorkflow
    ? (processInProgress / totalWorkflow) * 100
    : 0;
  const successRate = totalWorkflow
    ? ((processDone / totalWorkflow) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="bg-gray-50 flex items-center justify-center">
      <div className="w-full">
        <div className="bg-white rounded-md overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gray-100 rounded-lg p-2">
                <Activity className="w-5 h-5 text-gray-700" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-gray-900 text-lg font-bold">
                  Workflow Status
                </h3>
                <p className="text-gray-500 text-sm">
                  Live monitoring dashboard
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
              <p className="text-gray-500 text-xs font-medium">Success Rate</p>
              <p className="text-gray-900 text-2xl font-bold leading-none mt-1">
                {successRate}%
              </p>
            </div>
          </div>

          {/* Main circles */}
          <div className="p-6">
            <div className="flex items-center justify-between gap-6 mb-6">
              {/* ⭐ TOTAL WORKFLOW CIRCLE */}
              <div className="relative flex flex-col items-center flex-1">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#f3f4f6"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#3b82f6"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 52}`}
                      strokeDashoffset={`${2 * Math.PI * 52 * (1 - 100 / 100)}`}
                      className="transition-all duration-1000 ease-out"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900 tabular-nums">
                      {animatedTotal}
                    </span>
                  </div>
                  <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                    100%
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <Activity
                    className="w-5 h-5 text-blue-500 mx-auto mb-1"
                    strokeWidth={2.5}
                  />
                  <p className="text-sm font-semibold text-gray-700">Total</p>
                  <p className="text-xs text-gray-500">All workflows</p>
                </div>
              </div>

              {/* Completed */}
              <div className="relative flex flex-col items-center flex-1">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#f3f4f6"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#10b981"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 52}`}
                      strokeDashoffset={`${
                        2 * Math.PI * 52 * (1 - donePercentage / 100)
                      }`}
                      className="transition-all duration-1000 ease-out"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900 tabular-nums">
                      {animatedDone}
                    </span>
                  </div>
                  <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                    {donePercentage.toFixed(1)}%
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <TrendingUp
                    className="w-5 h-5 text-emerald-500 mx-auto mb-1"
                    strokeWidth={2.5}
                  />
                  <p className="text-sm font-semibold text-gray-700">
                    Completed
                  </p>
                </div>
              </div>

              {/* Failed */}
              <div className="relative flex flex-col items-center flex-1">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#f3f4f6"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#ef4444"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 52}`}
                      strokeDashoffset={`${
                        2 * Math.PI * 52 * (1 - failPercentage / 100)
                      }`}
                      className="transition-all duration-1000 ease-out"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900 tabular-nums">
                      {animatedFail}
                    </span>
                  </div>
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                    {failPercentage.toFixed(1)}%
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <AlertCircle
                    className="w-5 h-5 text-red-500 mx-auto mb-1"
                    strokeWidth={2.5}
                  />
                  <p className="text-sm font-semibold text-gray-700">Failed</p>
                </div>
              </div>

              {/* In Progress */}
              <div className="relative flex flex-col items-center flex-1">
                <div className="relative">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#f3f4f6"
                      strokeWidth="10"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="52"
                      stroke="#f59e0b"
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 52}`}
                      strokeDashoffset={`${
                        2 * Math.PI * 52 * (1 - inProgressPercentage / 100)
                      }`}
                      className="transition-all duration-1000 ease-out"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-gray-900 tabular-nums">
                      {animatedInProgress}
                    </span>
                  </div>
                  <div className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                    {inProgressPercentage.toFixed(1)}%
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <Loader2
                    className="w-5 h-5 text-amber-500 mx-auto mb-1 animate-spin"
                    strokeWidth={2.5}
                  />
                  <p className="text-sm font-semibold text-gray-700">
                    In Progress
                  </p>
                </div>
              </div>
            </div>

            {/* Distribution Bar (unchanged) */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-700">
                  Overall Distribution
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  {totalWorkflow} workflows
                </span>
              </div>

              <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden flex shadow-inner">
                <div
                  className="bg-emerald-500 transition-all duration-1000 ease-out"
                  style={{ width: `${donePercentage}%` }}
                />
                <div
                  className="bg-red-500 transition-all duration-1000 ease-out"
                  style={{ width: `${failPercentage}%` }}
                />
                <div
                  className="bg-amber-500 transition-all duration-1000 ease-out"
                  style={{ width: `${inProgressPercentage}%` }}
                />
              </div>

              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-emerald-500"></div>
                  <span className="text-xs text-gray-600 font-medium">
                    Completed {donePercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-red-500"></div>
                  <span className="text-xs text-gray-600 font-medium">
                    Failed {failPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-amber-500"></div>
                  <span className="text-xs text-gray-600 font-medium">
                    In Progress {inProgressPercentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowStatusCard;
